import React from 'react'
import { observer } from 'mobx-react-lite'
import { DateCalendar } from '../date-calendar'
import { Box, BoxProps, Flex, Text } from '../../vanilla'
import { useBasketStore, usePreBookStore } from '../../store/hooks/useStore'
import { ExclamationCircleIcon } from '../icons'
import { Link } from 'react-router-dom'
import { useDeliveryReservationTime } from '../../hooks/use-delivery-reservation-time'
import { useSlotExpiration } from '../../store/hooks/useSlotExpiration'
import { DeliveryTimeAndDate } from './delivery-time-and-date'

interface DeliveryDateTimeProps {
  popover?: boolean
  isBasket?: boolean
  hideDetails?: boolean
  calendarBoxProps?: BoxProps<React.ElementType>
  isCheckout?: boolean
  isPrebook?: boolean
}

const DeliveryDateTimeContainer = ({
  popover,
  isBasket,
  hideDetails = false,
  calendarBoxProps,
  isCheckout,
  isPrebook = false
}: DeliveryDateTimeProps) => {
  const preBookStore = usePreBookStore()
  const basketStore = useBasketStore()
  const {
    basket,
    shippingAddress,
    currentDeliveryDay,
    deliveryPostalCode,
  } = basketStore
  
  const { isAvailable } = currentDeliveryDay || isPrebook
  const { isReservationExpired } = useSlotExpiration()
  const deliveryReservationDuration = useDeliveryReservationTime()
  
  const postalCode = isPrebook ? preBookStore.preBookDeliveryPostalCode : deliveryPostalCode
  const shippingAddressId = isPrebook ? preBookStore.preBookDeliveryAddress?.addressId : shippingAddress?.c_customerAddressID
  const windowStartTime = isPrebook ? preBookStore.currentPreBookDeliverySlot?.windowStartTime : basket?.c_windowStartTime
  const windowEndTime = isPrebook ? preBookStore.currentPreBookDeliverySlot?.windowEndTime : basket?.c_windowEndTime

  return (
    <Flex
      gap="16px"
      paddingX="20px"
      align={popover || isBasket ? 'flex-start' : 'center'}
      paddingBottom="16px"
    >
      <DateCalendar
        date={new Date(windowStartTime)}
        size={popover ? 'md' : 'sm'}
        variant="accent"
        style={{opacity: !(isPrebook ? preBookStore.isPrebookReservationExpired() : isReservationExpired()) ? '1' : '0.2'}}
        {...calendarBoxProps}
      />
      {isBasket && (
        <Box>
          {(isPrebook ? preBookStore.isPrebookReservationExpired() : isReservationExpired()) ? (
            <>
              <Flex align="center" gap="4px">
                <ExclamationCircleIcon color="accent0" marginBottom="2px" flexShrink="0" />
                <Text color="accent0" variant="text4" data-test-selector="delivery-reservation-expired">
                  Your delivery reservation has expired!
                </Text>
              </Flex>

              <Text variant="text4" paddingLeft="20px">
                {deliveryReservationDuration}
              </Text>
            </>
          ) : (
            <Text color="accent0" variant="text4">
              Your delivery time
            </Text>
          )}
          <DeliveryTimeAndDate textProps={{variant: 'text4'}} showDate={true} showInline={true} />
        </Box>
      )}

      {!isBasket && !hideDetails && (
        <Box>
          {isAvailable || isPrebook ? (
            isCheckout || isPrebook ? (
              <>
                <Flex gap="4px">
                  <Text color="accent0" variant="text4">
                    When:
                  </Text>
                  <DeliveryTimeAndDate textProps={{variant: 'text4'}} />
                </Flex>

                <Flex gap="4px" flexWrap="wrap">
                  <Text color="accent0" variant="text4">
                    Where:
                  </Text>
                  <Text data-test-selector="delivery-address-summary" variant="text4" data-cs-mask="">
                    {postalCode} {shippingAddressId}
                  </Text>
                </Flex>

                <Text
                  as={Link}
                  to={isPrebook ? "/pre-book-delivery" : "/book-delivery"}
                  variant="text4"
                  textDecoration="underline"
                  color={{default: 'accent5'}}
                  data-test-selector="change-delivery-link"
                >
                  Change Delivery Time
                </Text>
              </>
            ) : (
              <>
                <Text color="accent0" variant="text4">
                  Your delivery time
                </Text>
                <DeliveryTimeAndDate textProps={{variant: 'text4'}} />
              </>
            )
          ) : (
            <>
              <Text color="accent0" variant="text4">
                Your chosen time is no longer available:
              </Text>
              <DeliveryTimeAndDate textProps={{variant: 'text4'}} />
            </>
          )}
        </Box>
      )}
    </Flex>
  )
}

export const DeliveryDateTime = observer(DeliveryDateTimeContainer)
